import axios from "axios";
import { URL, getToken } from "../utils";

/**
 * Récupère la liste des pays depuis l'API
 * @returns {Promise<Array>} Liste des pays formatée pour react-select
 */
export const fetchCountries = async () => {
  try {
    const token = getToken();
    const response = await axios.get(URL + "Clients/countries-list", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data.map((country) => ({
      value: country.code,
      label: country.name,
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des pays :", error);
    return [{ value: "TG", label: "Togo" }]; // Pays par défaut en cas d'erreur
  }
};

const defaultCountries = await fetchCountries();

/**
 * Normalise une chaîne pour comparer des pays sans tenir compte de la casse,
 * des accents ni des espaces superflus (ex: "Côte d'Ivoire" == "cote d ivoire").
 * @param {string} value
 * @returns {string}
 */
const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // supprime les accents
    .trim()
    .toLowerCase();

/**
 * Trouve le nom complet d'un pays à partir de son code
 * @param {string} countryCode - Code du pays
 * @param {Array} countries - Liste des pays
 * @returns {string} Nom du pays ou le code si non trouvé
 */
export const getCountryName = (countryCode, countries = defaultCountries) => {
  if (!countryCode) return countryCode;
  const target = normalize(countryCode);
  const country = countries.find(
    (c) => normalize(c.value) === target || normalize(c.label) === target,
  );
  return country ? country.label : countryCode;
};

/**
 * Trouve le code d'un pays à partir de son nom (ou de son code).
 * Ex: "Togo" -> "TG". Renvoie la valeur d'origine si non trouvée.
 * La comparaison ignore la casse et les accents.
 * @param {string} countryValue - Nom ou code du pays
 * @param {Array} countries - Liste des pays
 * @returns {string} Code du pays, ou la valeur d'origine si non trouvée
 */
export const getCountryCode = (countryValue, countries = defaultCountries) => {
  if (!countryValue) return "";
  const target = normalize(countryValue);
  const country = countries.find(
    (c) => normalize(c.value) === target || normalize(c.label) === target,
  );
  return country ? country.value : countryValue;
};

/**
 * Styles personnalisés pour react-select (pays)
 */
export const countrySelectStyles = {
  control: (base) => ({
    ...base,
    borderColor: "#d1d5db",
    boxShadow: "none",
    "&:hover": {
      borderColor: "#f59e0b",
    },
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? "#f59e0b" : "white",
    color: state.isSelected ? "white" : "#1f2937",
    "&:hover": {
      backgroundColor: "#fcd34d",
      color: "#1f2937",
    },
  }),
};
